import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * RevealText — masked line-by-line reveal.
 * Splits `lines` (array of strings) into masked containers with staggered enter.
 */
export const RevealText = ({ lines = [], as: Tag = "h1", className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });
  return (
    <Tag ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block">
          <span className="reveal-mask" aria-hidden={false}>
            <motion.span
              initial={{ y: "110%" }}
              animate={inView ? { y: "0%" } : { y: "110%" }}
              transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: delay + i * 0.12 }}
              className="inline-block will-change-transform"
            >
              {line}
            </motion.span>
          </span>
        </span>
      ))}
    </Tag>
  );
};

/**
 * FadeUp — simple staggered reveal wrapper.
 */
export const FadeUp = ({ children, delay = 0, y = 30, className = "", once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-10% 0px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/**
 * Counter — animates a number when visible.
 */
export const Counter = ({ value, className = "", suffix = "" }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    const num = typeof value === "number" ? value : parseFloat(String(value).replace(/[^\d.]/g, "")) || 0;
    let start = 0;
    const dur = 1600;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(num * eased));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, value]);
  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
};
